import {
  createUnvalidatedValidationSummary,
  safeParseCoasterProject,
  type AuthoredGridPose,
  type AuthoredGridSpan,
  type AuthoredTrackPieceInstance,
  type CoasterProject,
  type TrackBehavior,
} from '../schema/index.ts';
import { newStableId } from '../editor/operations.ts';
import { getAuthoredDescriptor } from './catalog.ts';
import {
  descriptorExitPose,
  fixedPointToWorld,
  instantiateSpans,
  posesConnect,
  posesEqual,
} from './fixed-grid.ts';
import {
  AUTHORED_CATALOG_VERSION,
  type AuthoredBuildResult,
  type AuthoredPieceCandidate,
  type AuthoredPieceDescriptor,
  type AuthoredTrackType,
} from './types.ts';
import { materializeAuthoredProject } from './materialize.ts';
import { authoredForward, authoredRight } from './directions.ts';

const RAIL_OVERLAP_UNITS = 6;
const ADJACENT_SEAM_ALLOWANCE_UNITS = 12;
const RAIL_SAMPLE_SPACING_UNITS = 2;

interface RailSample {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

type AuthoredTrack = Extract<CoasterProject['track'], { geometryKind: 'authored_grid_v2' }>;

function pieceMap(track: AuthoredTrack): ReadonlyMap<string, AuthoredTrackPieceInstance> {
  return new Map(track.pieces.map((piece) => [piece.id, piece]));
}

function activeComponent(track: AuthoredTrack, componentId?: string | null) {
  return (
    (componentId
      ? track.authoredTopology.components.find((component) => component.id === componentId)
      : null) ??
    track.authoredTopology.components[0] ??
    null
  );
}

function orderedPieces(track: AuthoredTrack, component = activeComponent(track)) {
  const byId = pieceMap(track);
  return component?.pieceIds.flatMap((id) => (byId.get(id) ? [byId.get(id)!] : [])) ?? [];
}

function activeTailPiece(
  track: AuthoredTrack,
  component = activeComponent(track),
): AuthoredTrackPieceInstance | null {
  return orderedPieces(track, component).at(-1) ?? null;
}

function activeHeadPiece(
  track: AuthoredTrack,
  component = activeComponent(track),
): AuthoredTrackPieceInstance | null {
  return orderedPieces(track, component)[0] ?? null;
}

function fixedDistance(first: RailSample, second: RailSample): number {
  return Math.hypot(first.x - second.x, first.y - second.y, first.z - second.z);
}

function cubicPoint(
  start: RailSample,
  control1: RailSample,
  control2: RailSample,
  end: RailSample,
  progress: number,
): RailSample {
  const inverse = 1 - progress;
  return {
    x:
      inverse ** 3 * start.x +
      3 * inverse ** 2 * progress * control1.x +
      3 * inverse * progress ** 2 * control2.x +
      progress ** 3 * end.x,
    y:
      inverse ** 3 * start.y +
      3 * inverse ** 2 * progress * control1.y +
      3 * inverse * progress ** 2 * control2.y +
      progress ** 3 * end.y,
    z:
      inverse ** 3 * start.z +
      3 * inverse ** 2 * progress * control1.z +
      3 * inverse * progress ** 2 * control2.z +
      progress ** 3 * end.z,
  };
}

function sampleRailSpans(
  entry: AuthoredGridPose,
  spans: readonly AuthoredGridSpan[],
): readonly RailSample[] {
  const samples: RailSample[] = [];
  let start: RailSample = entry;
  for (const [spanIndex, span] of spans.entries()) {
    const controlPolygonLength =
      fixedDistance(start, span.control1) +
      fixedDistance(span.control1, span.control2) +
      fixedDistance(span.control2, span.end);
    const steps = Math.max(
      4,
      Math.min(192, Math.ceil(controlPolygonLength / RAIL_SAMPLE_SPACING_UNITS)),
    );
    for (let step = spanIndex === 0 ? 0 : 1; step <= steps; step += 1)
      samples.push(cubicPoint(start, span.control1, span.control2, span.end, step / steps));
    start = span.end;
  }
  return samples;
}

function nearSeam(first: RailSample, second: RailSample, seam: RailSample): boolean {
  return (
    fixedDistance(first, seam) <= ADJACENT_SEAM_ALLOWANCE_UNITS &&
    fixedDistance(second, seam) <= ADJACENT_SEAM_ALLOWANCE_UNITS
  );
}

function candidateOverlapMessage(
  project: CoasterProject,
  candidate: AuthoredPieceCandidate,
  allowedSeams?: readonly { pieceId: string; pose: AuthoredGridPose }[],
): string | null {
  if (project.track.geometryKind !== 'authored_grid_v2' || !project.track.pieces.length)
    return null;
  const track = project.track;
  const candidateSamples = sampleRailSpans(candidate.entryPose, candidate.spans);
  const previousPiece = activeTailPiece(track);
  if (!previousPiece) return null;
  const seamTarget = track.authoredTopology.components
    .map((component) => orderedPieces(track, component)[0])
    .find((piece) => piece && posesConnect(candidate.exitPose, piece.entryPose));
  const seams = allowedSeams ?? [
    { pieceId: previousPiece.id, pose: candidate.entryPose },
    ...(seamTarget ? [{ pieceId: seamTarget.id, pose: seamTarget.entryPose }] : []),
  ];

  for (const existingPiece of project.track.pieces) {
    const existingSamples = sampleRailSpans(existingPiece.entryPose, existingPiece.spans);
    for (const candidateSample of candidateSamples) {
      for (const existingSample of existingSamples) {
        if (
          seams.some(
            (seam) =>
              seam.pieceId === existingPiece.id &&
              nearSeam(candidateSample, existingSample, seam.pose),
          )
        )
          continue;
        if (fixedDistance(candidateSample, existingSample) < RAIL_OVERLAP_UNITS)
          return getAuthoredDescriptor(existingPiece.descriptorId)?.category === 'station'
            ? 'This piece would overlap the station track. Match its visible orientation, bank, pitch, heading, and height before closing.'
            : 'This piece would overlap existing track. Choose another official piece or adjust the route.';
      }
    }
  }
  return null;
}

function behaviorFor(type: AuthoredTrackType): TrackBehavior {
  switch (type) {
    case 'station':
      return { kind: 'station', holdSeconds: 1.5, dispatchSpeedMps: 5 };
    case 'lift':
      return { kind: 'lift', targetSpeedMps: 5 };
    case 'launch':
      // A useful but broadly rideable V2 default: 30 mph. Creators can tune the
      // section after the circuit is complete without changing its authored shape.
      return { kind: 'launch', targetSpeedMps: 13.4112, accelerationMps2: 7 };
    case 'brake':
      return { kind: 'brake', targetSpeedMps: 9, decelerationMps2: 4 };
    case 'photo':
      return { kind: 'photo' };
    default:
      return { kind: 'normal' };
  }
}

function descriptorAllowsEntry(
  descriptor: AuthoredPieceDescriptor,
  pose: AuthoredGridPose,
): string | null {
  if (!descriptor.entry.pitches.includes(pose.pitchDegrees))
    return `${descriptor.label} cannot begin on a ${pose.pitchDegrees}° track.`;
  if (!descriptor.entry.banks.includes(pose.bank))
    return `${descriptor.label} requires ${descriptor.entry.banks.join(' or ')} banking.`;
  if (!descriptor.entry.orientations.includes(pose.orientation))
    return `${descriptor.label} requires ${descriptor.entry.orientations.join(' or ')} track.`;
  return null;
}

export function authoredEndPose(
  project: CoasterProject,
  componentId?: string | null,
): AuthoredGridPose | null {
  if (project.track.geometryKind !== 'authored_grid_v2') return null;
  const component = activeComponent(project.track, componentId);
  return activeTailPiece(project.track, component)?.exitPose ?? null;
}

export function authoredStartPose(
  project: CoasterProject,
  componentId?: string | null,
): AuthoredGridPose | null {
  if (project.track.geometryKind !== 'authored_grid_v2') return null;
  const component = activeComponent(project.track, componentId);
  return activeHeadPiece(project.track, component)?.entryPose ?? null;
}

export function authoredSequenceIssue(
  project: CoasterProject,
  descriptorId: string,
  componentId?: string | null,
): string | null {
  if (project.track.geometryKind !== 'authored_grid_v2') return null;
  const previousId = activeTailPiece(
    project.track,
    activeComponent(project.track, componentId),
  )?.descriptorId;
  if (!previousId) return null;
  const previous = getAuthoredDescriptor(previousId);
  const next = getAuthoredDescriptor(descriptorId);
  if (previous?.family === 'half_loop' && next?.family === 'half_loop')
    return 'Half Loops cannot connect directly because their planar paths would overlap. Continue inverted with a Half Roll or Half Corkscrew.';
  return null;
}

export function createAuthoredCandidate(
  descriptorId: string,
  entryPose: AuthoredGridPose,
  trackType: AuthoredTrackType = 'normal',
): AuthoredBuildResult<AuthoredPieceCandidate> {
  const descriptor = getAuthoredDescriptor(descriptorId);
  if (!descriptor)
    return {
      ok: false,
      code: 'unknown_descriptor',
      message: 'That track piece is not available in this catalog version.',
    };
  const entryFailure = descriptorAllowsEntry(descriptor, entryPose);
  if (entryFailure) return { ok: false, code: 'incompatible_entry', message: entryFailure };
  if (!descriptor.allowedTrackTypes.includes(trackType))
    return {
      ok: false,
      code: 'incompatible_track_type',
      message: `${descriptor.label} cannot use ${trackType} track.`,
    };
  return {
    ok: true,
    value: {
      descriptor,
      entryPose,
      exitPose: descriptorExitPose(entryPose, descriptor),
      spans: instantiateSpans(entryPose, descriptor),
      behavior: behaviorFor(trackType),
    },
  };
}

/** Resolve an official descriptor whose exit lands exactly on an exposed component head. */
export function createAuthoredCandidateEndingAt(
  descriptorId: string,
  exitTarget: AuthoredGridPose,
  trackType: AuthoredTrackType = 'normal',
): AuthoredBuildResult<AuthoredPieceCandidate> {
  const descriptor = getAuthoredDescriptor(descriptorId);
  if (!descriptor)
    return {
      ok: false,
      code: 'unknown_descriptor',
      message: 'That track piece is not available in this catalog version.',
    };
  const last = descriptor.spans.at(-1);
  if (!last)
    return { ok: false, code: 'damaged_descriptor', message: 'That track piece has no geometry.' };
  for (const pitchDegrees of descriptor.entry.pitches) {
    for (const bank of descriptor.entry.banks) {
      for (const orientation of descriptor.entry.orientations) {
        const headingStep = (exitTarget.headingStep - descriptor.exit.headingDelta + 80) % 8;
        const forward = authoredForward(headingStep);
        const right = authoredRight(headingStep);
        const entryPose: AuthoredGridPose = {
          x: exitTarget.x - last.end.x * forward.x - last.end.z * right.x,
          y: exitTarget.y - last.end.y,
          z: exitTarget.z - last.end.x * forward.z - last.end.z * right.z,
          headingStep,
          pitchDegrees,
          bank,
          orientation,
          windingQuarterTurns:
            exitTarget.windingQuarterTurns - descriptor.exit.windingQuarterTurnsDelta,
        };
        const candidate = createAuthoredCandidate(descriptorId, entryPose, trackType);
        if (candidate.ok && posesConnect(candidate.value.exitPose, exitTarget)) return candidate;
      }
    }
  }
  return {
    ok: false,
    code: 'incompatible_exit',
    message: `${descriptor.label} cannot end at this exposed track connector.`,
  };
}

function resetDerivedProjectState(project: CoasterProject): CoasterProject {
  const next = {
    ...project,
    revisionId: newStableId('revision'),
    metadata: { ...project.metadata, updatedAt: new Date().toISOString() },
    validation: createUnvalidatedValidationSummary(),
    highlights: [],
  };
  delete next.successfulTest;
  return next;
}

export function appendAuthoredCandidate(
  project: CoasterProject,
  candidate: AuthoredPieceCandidate,
  componentId?: string | null,
): AuthoredBuildResult<CoasterProject> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This operation is only available in the V2 authored-grid builder.',
    };
  const currentEnd = authoredEndPose(project, componentId);
  if (currentEnd && !posesEqual(currentEnd, candidate.entryPose))
    return {
      ok: false,
      code: 'entry_mismatch',
      message: 'The preview no longer matches the current track exit.',
    };
  if (!currentEnd && project.track.pieces.length !== 0)
    return {
      ok: false,
      code: 'damaged_piece_list',
      message: 'The authored piece list has no valid track exit.',
    };
  const sequenceIssue = authoredSequenceIssue(project, candidate.descriptor.id, componentId);
  if (sequenceIssue)
    return {
      ok: false,
      code: 'incompatible_sequence',
      message: sequenceIssue,
    };
  const track = project.track;
  const component = activeComponent(track, componentId);
  const previousPiece = activeTailPiece(track, component);
  const targetComponent = track.authoredTopology.components.find((candidateComponent) => {
    if (candidateComponent.id === component?.id) return false;
    const targetPiece = orderedPieces(track, candidateComponent)[0];
    return Boolean(targetPiece && posesConnect(candidate.exitPose, targetPiece.entryPose));
  });
  const ownHead = component ? orderedPieces(track, component)[0] : null;
  const targetHead =
    (targetComponent ? orderedPieces(track, targetComponent)[0] : null) ??
    (ownHead && posesConnect(candidate.exitPose, ownHead.entryPose) ? ownHead : null);
  const overlapMessage = candidateOverlapMessage(project, candidate, [
    ...(previousPiece ? [{ pieceId: previousPiece.id, pose: candidate.entryPose }] : []),
    ...(targetHead ? [{ pieceId: targetHead.id, pose: targetHead.entryPose }] : []),
  ]);
  if (overlapMessage)
    return {
      ok: false,
      code: 'track_overlap',
      message: overlapMessage,
    };

  const pieceId = newStableId('authored-piece');
  const previousPieceForSection = activeTailPiece(track, component);
  const previousSectionId = previousPieceForSection?.sectionIds.at(-1);
  const startingNodeId =
    project.track.sections.find((section) => section.id === previousSectionId)?.toNodeId ??
    newStableId('authored-node');
  const nodes = [...project.track.nodes];
  if (!nodes.length)
    nodes.push({
      id: startingNodeId,
      position: fixedPointToWorld(candidate.entryPose),
      bankDegrees: candidate.spans[0]?.bankStartDegrees ?? 0,
      rollWindingDegrees: candidate.spans[0]?.windingStartDegrees ?? 0,
    });

  const sections = [...project.track.sections];
  const sectionIds: string[] = [];
  let fromNodeId = startingNodeId;
  candidate.spans.forEach((span) => {
    const nodeId = newStableId('authored-node');
    const sectionId = newStableId('authored-section');
    nodes.push({
      id: nodeId,
      position: fixedPointToWorld(span.end),
      bankDegrees: Math.max(-90, Math.min(90, span.bankEndDegrees)),
      rollWindingDegrees: span.windingEndDegrees,
    });
    sections.push({
      id: sectionId,
      fromNodeId,
      toNodeId: nodeId,
      behavior: structuredClone(candidate.behavior),
      geometry: {
        kind: 'cubic_bezier',
        control1: fixedPointToWorld(span.control1),
        control2: fixedPointToWorld(span.control2),
      },
    });
    sectionIds.push(sectionId);
    fromNodeId = nodeId;
  });

  const next = resetDerivedProjectState({
    ...project,
    track: {
      ...project.track,
      nodes,
      sections,
      pieces: [
        ...project.track.pieces,
        {
          id: pieceId,
          descriptorId: candidate.descriptor.id,
          catalogVersion: AUTHORED_CATALOG_VERSION,
          entryPose: candidate.entryPose,
          exitPose: candidate.exitPose,
          sectionIds,
          spans: candidate.spans.map((span) => ({ ...span })),
          behavior: structuredClone(candidate.behavior),
        },
      ],
      authoredTopology: {
        version: 1,
        components: component
          ? project.track.authoredTopology.components.flatMap((candidateComponent) => {
              if (candidateComponent.id === targetComponent?.id) return [];
              if (candidateComponent.id !== component.id) return [candidateComponent];
              return [
                {
                  ...candidateComponent,
                  pieceIds: [
                    ...candidateComponent.pieceIds,
                    pieceId,
                    ...(targetComponent?.pieceIds ?? []),
                  ],
                },
              ];
            })
          : [{ id: newStableId('authored-component'), pieceIds: [pieceId], closed: false }],
      },
    },
    editor: {
      ...project.editor,
      pieceGroups: [
        ...project.editor.pieceGroups,
        {
          id: pieceId,
          definitionId: 'custom',
          sectionIds,
          parameters: null,
        },
      ],
    },
  });
  const parsed = safeParseCoasterProject(materializeAuthoredProject(next));
  if (!parsed.success)
    return {
      ok: false,
      code: 'schema_rejected_candidate',
      message: `The authored piece failed project validation: ${parsed.error.issues[0]?.message ?? 'unknown schema issue'}`,
    };
  const authoredTrack =
    parsed.data.track.geometryKind === 'authored_grid_v2' ? parsed.data.track : null;
  const activePieces = authoredTrack ? orderedPieces(authoredTrack) : [];
  const firstPiece = activePieces[0] ?? null;
  const lastPiece = activePieces.at(-1) ?? null;
  if (
    firstPiece &&
    lastPiece &&
    authoredTrack &&
    authoredTrack.pieces.length >= 4 &&
    posesConnect(lastPiece.exitPose, firstPiece.entryPose)
  )
    return closeAuthoredCircuit(parsed.data);
  return { ok: true, value: parsed.data };
}

export function prependAuthoredCandidate(
  project: CoasterProject,
  candidate: AuthoredPieceCandidate,
  componentId?: string | null,
): AuthoredBuildResult<CoasterProject> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This operation is only available in the V2 authored-grid builder.',
    };
  const track = project.track;
  const component = activeComponent(track, componentId);
  const currentHead = activeHeadPiece(track, component);
  if (!component || !currentHead || !posesConnect(candidate.exitPose, currentHead.entryPose))
    return {
      ok: false,
      code: 'exit_mismatch',
      message: 'The preview no longer matches the selected beginning connector.',
    };
  const targetComponent = track.authoredTopology.components.find((candidateComponent) => {
    if (candidateComponent.id === component.id) return false;
    const targetTail = orderedPieces(track, candidateComponent).at(-1);
    return Boolean(targetTail && posesConnect(targetTail.exitPose, candidate.entryPose));
  });
  const targetTail = targetComponent ? orderedPieces(track, targetComponent).at(-1) : null;
  const overlapMessage = candidateOverlapMessage(project, candidate, [
    { pieceId: currentHead.id, pose: currentHead.entryPose },
    ...(targetTail ? [{ pieceId: targetTail.id, pose: candidate.entryPose }] : []),
  ]);
  if (overlapMessage) return { ok: false, code: 'track_overlap', message: overlapMessage };

  const pieceId = newStableId('authored-piece');
  const nodes = [...track.nodes];
  const sections = [...track.sections];
  const sectionIds: string[] = [];
  let fromNodeId = newStableId('authored-node');
  nodes.push({
    id: fromNodeId,
    position: fixedPointToWorld(candidate.entryPose),
    bankDegrees: candidate.spans[0]?.bankStartDegrees ?? 0,
    rollWindingDegrees: candidate.spans[0]?.windingStartDegrees ?? 0,
  });
  for (const span of candidate.spans) {
    const toNodeId = newStableId('authored-node');
    const sectionId = newStableId('authored-section');
    nodes.push({
      id: toNodeId,
      position: fixedPointToWorld(span.end),
      bankDegrees: Math.max(-90, Math.min(90, span.bankEndDegrees)),
      rollWindingDegrees: span.windingEndDegrees,
    });
    sections.push({
      id: sectionId,
      fromNodeId,
      toNodeId,
      behavior: structuredClone(candidate.behavior),
      geometry: {
        kind: 'cubic_bezier',
        control1: fixedPointToWorld(span.control1),
        control2: fixedPointToWorld(span.control2),
      },
    });
    sectionIds.push(sectionId);
    fromNodeId = toNodeId;
  }
  const next = resetDerivedProjectState({
    ...project,
    track: {
      ...track,
      nodes,
      sections,
      pieces: [
        ...track.pieces,
        {
          id: pieceId,
          descriptorId: candidate.descriptor.id,
          catalogVersion: AUTHORED_CATALOG_VERSION,
          entryPose: candidate.entryPose,
          exitPose: candidate.exitPose,
          sectionIds,
          spans: candidate.spans.map((span) => ({ ...span })),
          behavior: structuredClone(candidate.behavior),
        },
      ],
      authoredTopology: {
        version: 1,
        components: track.authoredTopology.components.flatMap((candidateComponent) => {
          if (candidateComponent.id === targetComponent?.id) return [];
          if (candidateComponent.id !== component.id) return [candidateComponent];
          return [
            {
              ...candidateComponent,
              pieceIds: [
                ...(targetComponent?.pieceIds ?? []),
                pieceId,
                ...candidateComponent.pieceIds,
              ],
            },
          ];
        }),
      },
    },
    editor: {
      ...project.editor,
      pieceGroups: [
        ...project.editor.pieceGroups,
        { id: pieceId, definitionId: 'custom', sectionIds, parameters: null },
      ],
    },
  });
  const parsed = safeParseCoasterProject(materializeAuthoredProject(next));
  if (!parsed.success)
    return {
      ok: false,
      code: 'schema_rejected_candidate',
      message: `The authored piece failed project validation: ${parsed.error.issues[0]?.message ?? 'unknown schema issue'}`,
    };
  if (parsed.data.track.geometryKind !== 'authored_grid_v2')
    return { ok: true, value: parsed.data };
  const activePieces = orderedPieces(parsed.data.track);
  const firstPiece = activePieces[0];
  const lastPiece = activePieces.at(-1);
  if (
    parsed.data.track.authoredTopology.components.length === 1 &&
    firstPiece &&
    lastPiece &&
    activePieces.length >= 4 &&
    posesConnect(lastPiece.exitPose, firstPiece.entryPose)
  )
    return closeAuthoredCircuit(parsed.data);
  return { ok: true, value: parsed.data };
}

export function appendAuthoredPiece(
  project: CoasterProject,
  descriptorId: string,
  entryPose: AuthoredGridPose | null = authoredEndPose(project),
  trackType: AuthoredTrackType = 'normal',
): AuthoredBuildResult<CoasterProject> {
  if (!entryPose)
    return {
      ok: false,
      code: 'station_required',
      message: 'Place a station before adding track.',
    };
  const candidate = createAuthoredCandidate(descriptorId, entryPose, trackType);
  return candidate.ok ? appendAuthoredCandidate(project, candidate.value) : candidate;
}

export function removeLastAuthoredPiece(
  project: CoasterProject,
): AuthoredBuildResult<CoasterProject> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This operation is only available in the V2 authored-grid builder.',
    };
  const piece = activeTailPiece(project.track);
  if (!piece)
    return { ok: false, code: 'empty_track', message: 'There is no track piece to remove.' };
  const removeSections = new Set(piece.sectionIds);
  const remainingSections = project.track.sections.filter(
    (section) => !removeSections.has(section.id),
  );
  const remainingNodeIds = new Set<string>();
  remainingSections.forEach((section) => {
    remainingNodeIds.add(section.fromNodeId);
    remainingNodeIds.add(section.toNodeId);
  });
  const remainingPieces = project.track.pieces.filter((candidate) => candidate.id !== piece.id);
  if (!remainingSections.length && remainingPieces.length === 0) remainingNodeIds.clear();
  const next = resetDerivedProjectState({
    ...project,
    track: {
      ...project.track,
      closed: false,
      pieces: remainingPieces,
      authoredTopology: {
        version: 1,
        components: project.track.authoredTopology.components
          .map((component) => ({
            ...component,
            closed: false,
            pieceIds: component.pieceIds.filter((pieceId) => pieceId !== piece.id),
          }))
          .filter((component) => component.pieceIds.length > 0),
      },
      sections: remainingSections,
      nodes: project.track.nodes.filter((node) => remainingNodeIds.has(node.id)),
    },
    editor: {
      ...project.editor,
      pieceGroups: project.editor.pieceGroups.filter((group) => group.id !== piece.id),
    },
  });
  const parsed = safeParseCoasterProject(materializeAuthoredProject(next));
  return parsed.success
    ? { ok: true, value: parsed.data }
    : {
        ok: false,
        code: 'schema_rejected_delete',
        message: 'The last piece could not be removed safely.',
      };
}

export function removeSelectedAuthoredPiece(
  project: CoasterProject,
  pieceId: string,
): AuthoredBuildResult<CoasterProject> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This operation is only available in the V2 authored-grid builder.',
    };
  const piece = project.track.pieces.find((candidate) => candidate.id === pieceId);
  if (!piece)
    return { ok: false, code: 'unknown_piece', message: 'That track piece no longer exists.' };
  if (getAuthoredDescriptor(piece.descriptorId)?.category === 'station')
    return {
      ok: false,
      code: 'station_protected',
      message: 'The station is protected. Select an ordinary track piece to replace it.',
    };
  const componentIndex = project.track.authoredTopology.components.findIndex((component) =>
    component.pieceIds.includes(pieceId),
  );
  const component = project.track.authoredTopology.components[componentIndex];
  if (!component)
    return {
      ok: false,
      code: 'damaged_topology',
      message: 'That piece has no construction component.',
    };
  const pieceIndex = component.pieceIds.indexOf(pieceId);
  const before = component.pieceIds.slice(0, pieceIndex);
  const after = component.pieceIds.slice(pieceIndex + 1);
  const replacementComponents = component.closed
    ? after.length || before.length
      ? [{ ...component, closed: false, pieceIds: [...after, ...before] }]
      : []
    : [
        ...(before.length ? [{ ...component, closed: false, pieceIds: before }] : []),
        ...(after.length
          ? [
              {
                id: before.length ? newStableId('authored-component') : component.id,
                closed: false,
                pieceIds: after,
              },
            ]
          : []),
      ];
  const removeSections = new Set(piece.sectionIds);
  const sections = project.track.sections.filter((section) => !removeSections.has(section.id));
  const nodeIds = new Set<string>();
  sections.forEach((section) => {
    nodeIds.add(section.fromNodeId);
    nodeIds.add(section.toNodeId);
  });
  const next = resetDerivedProjectState({
    ...project,
    track: {
      ...project.track,
      closed: false,
      pieces: project.track.pieces.filter((candidate) => candidate.id !== piece.id),
      sections,
      nodes: project.track.nodes.filter((node) => nodeIds.has(node.id)),
      authoredTopology: {
        version: 1,
        components: project.track.authoredTopology.components.flatMap((candidate, index) =>
          index === componentIndex ? replacementComponents : [{ ...candidate, closed: false }],
        ),
      },
    },
    supports: {
      ...project.supports,
      suppressedSectionIds: project.supports.suppressedSectionIds.filter(
        (sectionId) => !removeSections.has(sectionId),
      ),
      manual: project.supports.manual.filter((support) => !removeSections.has(support.sectionId)),
    },
    editor: {
      ...project.editor,
      pieceGroups: project.editor.pieceGroups.filter((group) => group.id !== piece.id),
    },
  });
  const parsed = safeParseCoasterProject(materializeAuthoredProject(next));
  return parsed.success
    ? { ok: true, value: parsed.data }
    : {
        ok: false,
        code: 'schema_rejected_delete',
        message: `That piece could not be removed safely: ${parsed.error.issues[0]?.message ?? 'unknown topology error'}`,
      };
}

export function closeAuthoredCircuit(project: CoasterProject): AuthoredBuildResult<CoasterProject> {
  if (project.track.geometryKind !== 'authored_grid_v2')
    return {
      ok: false,
      code: 'wrong_geometry_kind',
      message: 'This operation is only available in the V2 authored-grid builder.',
    };
  if (project.track.authoredTopology.components.length !== 1)
    return {
      ok: false,
      code: 'disconnected_components',
      message: 'Repair every exposed track gap before closing the circuit.',
    };
  const component = activeComponent(project.track);
  const componentPieces = orderedPieces(project.track, component);
  const station = componentPieces.find(
    (piece) => getAuthoredDescriptor(piece.descriptorId)?.category === 'station',
  );
  const end = componentPieces.at(-1);
  if (
    !station ||
    !component ||
    componentPieces.filter(
      (piece) => getAuthoredDescriptor(piece.descriptorId)?.category === 'station',
    ).length !== 1 ||
    !end
  )
    return {
      ok: false,
      code: 'station_required',
      message: 'A circuit must begin with a station.',
    };
  if (!posesConnect(end.exitPose, componentPieces[0]!.entryPose))
    return {
      ok: false,
      code: 'not_aligned',
      message: 'The track exit must exactly match the station entry pose.',
    };
  if (project.track.nodes.length < 4)
    return {
      ok: false,
      code: 'too_short',
      message: 'Add more track before closing the circuit.',
    };
  const firstNode = project.track.nodes[0]!;
  const lastNode = project.track.nodes.at(-1)!;
  const sections = project.track.sections.map((section, index, source) =>
    index === source.length - 1 ? { ...section, toNodeId: firstNode.id } : section,
  );
  const nodes = project.track.nodes.filter((node) => node.id !== lastNode.id);
  const stationIndex = componentPieces.findIndex((piece) => piece.id === station.id);
  const stationRootedPieceIds = [
    ...componentPieces.slice(stationIndex),
    ...componentPieces.slice(0, stationIndex),
  ].map((piece) => piece.id);
  const next = resetDerivedProjectState({
    ...project,
    track: {
      ...project.track,
      closed: true,
      nodes,
      sections,
      authoredTopology: {
        version: 1,
        components: [{ ...component!, closed: true, pieceIds: stationRootedPieceIds }],
      },
    },
  });
  const parsed = safeParseCoasterProject(materializeAuthoredProject(next));
  return parsed.success
    ? { ok: true, value: parsed.data }
    : {
        ok: false,
        code: 'schema_rejected_closure',
        message: 'The exact circuit could not be compiled.',
      };
}
